<script setup lang="ts">
import type { SbInfoCardStoryblok } from '~/types/storyblok'
import { CfgTypography, CfgTypographySizes } from '@cfgtech/ui'

const { blok } = defineProps<{ blok: SbInfoCardStoryblok }>()

const renderedTitle = computed(() => blok.title?.content?.length ? renderRichText(blok.title) : null)
const renderedDescription = computed(() => blok.description?.content?.length ? renderRichText(blok.description) : null)

function getImageSize(filename?: string) {
  const [width = '0', height = '0'] = filename?.split('/')[5].split('x') || []
  return {
    width: Number.parseInt(width),
    height: Number.parseInt(height),
  }
}

const desktopImageSize = computed(() => getImageSize(blok.desktopImage?.filename))
const mobileImageSize = computed(() => getImageSize(blok.mobileImage?.filename))

const isTop = computed(() => blok.imagePosition === 'top')
const isBottom = computed(() => blok.imagePosition === 'bottom')
const isLeft = computed(() => blok.imagePosition === 'left')
const isRight = computed(() => blok.imagePosition === 'right')

const isHorizontal = computed(() => isLeft.value || isRight.value)
const isVertical = computed(() => isTop.value || isBottom.value)

type Variant = {
  background: string
  highlight: string
}

const variantClasses = computed<Variant>(() => {
  switch (blok.variant) {
    case 'blue':
      return {
        background: 'bg-[#E8F5FA]',
        highlight: '[&>.highlight]:text-brand',
      }
    case 'orange':
      return {
        background: 'bg-[#F4A64C26]',
        highlight: '[&>.highlight]:text-[#F4A64C]',
      }
    case 'neutral':
    default:
      return {
        background: 'bg-[#F4F3F0]',
        highlight: '',
      }
  }
})
</script>

<template>
  <div
    class="flex size-full flex-col justify-between rounded-xl"
    :class="{
      'md:flex-col': isTop,
      'md:flex-col-reverse': isVertical,
      'md:flex-row-reverse': isRight,
      'md:flex-row': isLeft,
      [variantClasses.background]: true,
    }"
  >
    <div
      class="shrink-0 grow"
      :class="{
        'w-1/3': isHorizontal,
      }"
    >
      <img
        :alt="blok.desktopImage?.alt || undefined"
        class="hidden size-full object-contain object-center md:block"
        :height="desktopImageSize.height"
        loading="lazy"
        :src="blok.desktopImage?.filename"
        :width="desktopImageSize.width"
      >
    </div>

    <img
      :alt="blok.mobileImage?.alt || undefined"
      class="block w-auto self-start md:hidden"
      :height="mobileImageSize.height"
      loading="lazy"
      :src="blok.mobileImage?.filename"
      :width="mobileImageSize.width"
    >

    <div
      class="space-y-3 pb-6 max-md:px-5"
      :class="{
        'md:px-8 md:pb-8': isTop,
        'md:px-8 md:pt-8': isBottom,
        'md:py-8 md:pr-8': isLeft,
        'md:py-8 md:pl-8': isRight,
      }"
    >
      <TypographyStyledText v-if="renderedTitle">
        <TypographySanitizedHTML
          v-if="renderedTitle"
          :allowed-classes="['text-brand', 'text-white', 'highlight']"
          :allowed-tags="['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'b', 'strong', 'br', 'a']"
          :html="renderedTitle"
        >
          <template #default="{ html }">
            <CfgTypography
              class="text-pretty !font-extrabold"
              :class="variantClasses.highlight"
              :html="html"
              :size="CfgTypographySizes.h2"
              tag-name="div"
            />
          </template>
        </TypographySanitizedHTML>
      </TypographyStyledText>

      <TypographyStyledText v-if="renderedDescription">
        <TypographySanitizedHTML
          v-if="renderedDescription"
          :allowed-classes="['text-brand', 'text-white']"
          :allowed-tags="['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'b', 'strong', 'br', 'a', 'p']"
          :html="renderedDescription"
        >
          <template #default="{ html }">
            <CfgTypography
              class="text-pretty"
              :html="html"
              :size="CfgTypographySizes.md"
              tag-name="div"
            />
          </template>
        </TypographySanitizedHTML>
      </TypographyStyledText>
    </div>
  </div>
</template>
